import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Space, Tag, Spin, Alert, Descriptions, Tabs } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getEmailTemplate, type EmailTemplate } from '@/api/adminApi';
import { useAuth } from '@/hooks/useAuth';

export default function EmailTemplatePreview() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canRead = hasPermission('admin', 'read');
  const canWrite = hasPermission('admin', 'write');

  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getEmailTemplate(id)
      .then((data) => setTemplate(data))
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (!canRead) return <Alert type="error" message={t('app.forbidden')} />;
  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  if (!template) return <Alert type="warning" message={t('app.not_found')} />;

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/email-templates')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button icon={<EditOutlined />} onClick={() => navigate(`/admin/email-templates/${id}/edit`)}>
            {t('app.edit')}
          </Button>
        )}
      </Space>

      <h2>{t('admin.email_template_detail_title')}</h2>

      <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
        <Descriptions.Item label={t('admin.email_template_field.name')}>{template.name}</Descriptions.Item>
        <Descriptions.Item label={t('admin.email_template_field.type')}>
          <Tag>{t(`admin.email_template_type.${template.type}`, template.type)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.email_template_field.subject')} span={2}>{template.subject}</Descriptions.Item>
        <Descriptions.Item label={t('admin.email_template_field.is_active')}>
          <Tag color={template.is_active ? 'green' : 'default'}>
            {template.is_active ? t('app.yes') : t('app.no')}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.email_template_field.description')}>{template.description || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('admin.email_template_field.variables')} span={2}>
          {template.variables && template.variables.length > 0
            ? template.variables.map((v) => <Tag key={v}>{`{{${v}}}`}</Tag>)
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.field.created_at')}>
          {template.created_at ? new Date(template.created_at).toLocaleString() : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('admin.field.updated_at')}>
          {template.updated_at ? new Date(template.updated_at).toLocaleString() : '-'}
        </Descriptions.Item>
      </Descriptions>

      <Tabs
        defaultActiveKey="html"
        items={[
          {
            key: 'html',
            label: t('admin.email_template_field.body_html'),
            children: (
              <iframe
                srcDoc={template.body_html}
                sandbox=""
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 8,
                  width: '100%',
                  minHeight: 300,
                  background: '#fff',
                }}
                title={t('admin.email_template_field.body_html')}
              />
            ),
          },
          {
            key: 'source',
            label: t('admin.preview_source'),
            children: (
              <pre style={{
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                padding: 16,
                minHeight: 200,
                background: '#fafafa',
                overflow: 'auto',
                maxHeight: 500,
              }}>
                {template.body_html}
              </pre>
            ),
          },
          ...(template.body_text
            ? [
                {
                  key: 'text',
                  label: t('admin.email_template_field.body_text'),
                  children: (
                    <pre style={{
                      border: '1px solid #d9d9d9',
                      borderRadius: 8,
                      padding: 16,
                      minHeight: 200,
                      background: '#fafafa',
                      overflow: 'auto',
                      maxHeight: 500,
                    }}>
                      {template.body_text}
                    </pre>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}
